import { DateTime } from 'luxon';
import type { IWorkingHours, IWorkingHoursDay } from '@interfaces/IWorkingHours';
import { formatYmd } from '@utils/dateUtils';
import { isWorkingDay, restDaysFromWorkingHours } from '@utils/workCalendar';
import type { IReplayShift } from './ReplayTrack';

/**
 * How many days before today can be replayed: today plus 29 (the last 30
 * days). Must match the backend's `REPLAY_DAYS_BACK` (core/monitor/replay/window.ts),
 * which rejects anything older.
 */
export const REPLAY_LOOKBACK_DAYS = 29;

/** `days` is Monday-first and always 7 long when `available`. */
const dayOf = (wh: IWorkingHours, d: DateTime) => wh.days[d.weekday - 1] as IWorkingHoursDay;

/**
 * The `count` most recent days before `today` that the site works
 * (`dailyWorkerHours > 0`), newest first, within the lookback. With no known
 * schedule every day counts.
 */
export function recentWorkingDays(today: DateTime, wh: IWorkingHours | undefined, count = 2): string[] {
  const restDays = wh ? restDaysFromWorkingHours(wh) : new Set<number>();
  const out: string[] = [];
  for (let i = 1; i <= REPLAY_LOOKBACK_DAYS && out.length < count; i++) {
    const date = formatYmd(today.minus({ days: i }), today.zone.name);
    if (isWorkingDay(date, restDays)) out.push(date);
  }
  return out;
}

/** That weekday's shift in the site zone, or null on a day off / without times. */
export function shiftFor(date: string, tz: string, wh: IWorkingHours | undefined): IReplayShift | null {
  if (!wh?.available) return null;
  const day = dayOf(wh, DateTime.fromISO(date, { zone: tz }));
  if (!day.startDayTime || !day.endDayTime || day.dailyWorkerHours <= 0) return null;
  const at = (hhmm: string) => DateTime.fromISO(`${date}T${hhmm}`, { zone: tz }).toMillis();
  return { startMs: at(day.startDayTime), endMs: at(day.endDayTime) };
}
